import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import useFormStore from "../state/useFormStore";
import { Box, Button, TextField, Typography, Chip } from "@mui/material";
import { apiService } from '../utils/api';
import { config } from "../config";
import Lottie from 'lottie-react';
import money from '../assets/money.json';
import banking from '../assets/faster.json';
import withAuthAndData from '../components/withAuthAndData';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import { formatCurrency as formatCurrencyUtil } from '../utils/utils';
import { bankOptions } from '../data/selectOptions';

const Wrapper = styled.div`
  height: 100vh;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 24px;
  padding-bottom: 100px;
`;

const MainText = styled.div`
  margin: 30px 0 20px 0;
  font-size: 24px;
  text-align: center;
  line-height: 32px;
  font-weight: 600;
  color: #333;
`;

const Subtext = styled.h4`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  margin: 0 0 20px 0;
  text-align: center;
  color: #666;
  width: 100%;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
  max-width: 500px;
`;

const CardButton = styled(Box)`
  position: relative;
  cursor: pointer;
  border: 2px solid #95d0dd;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  background-color: transparent;

  &:hover,
  &:focus,
  &:active {
    background-color: #e4faff;
    border-color: #fff200;
  }
`;

const OptionalBadge = styled(Chip)`
  position: absolute;
  top: 45px;
  right: 8px;
  background-color: #e8f0fe !important;
  color: #5c50fc !important;
  font-size: 8px;
  font-weight: 600;
  height: 20px;
`;

interface VerificationButtonProps {
  active?: boolean;
}

const VerificationButton = styled(Button)<VerificationButtonProps>`
  width: 100%;
  padding: 16px !important;
  text-align: left !important;
  margin-bottom: 16px !important;
  background-color: ${({ active }) => active ? '#95d0dd' : 'transparent'} !important;
  border: 2px solid ${({ active }) => active ? '#1e6373' : '#95d0dd'} !important;
  color: #000 !important;
  border-radius: 12px !important;
  transition: all 0.2s ease !important;

  &:hover {
    background-color: ${({ active }) => active ? '#5ab1c4' : '#e4faff'} !important;
    border-color: ${({ active }) => active ? '#1e6373' : '#95d0dd'} !important;
  }
`;

interface IconWrapperProps {
  active?: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${({ active }) => active ? '#000' : '#95d0dd'};
  border-radius: 50%;
  color: white;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const TextWrapper = styled.div`
  text-align: left;
`;

const ButtonTitle = styled(Typography)`
  font-size: 16px !important;
  font-weight: 600 !important;
  margin-bottom: 4px !important;
`;

const ButtonSubtitle = styled(Typography)`
  font-size: 12px !important;
  color: #666 !important;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const InfoIcon = styled.div`
  margin-top: 3px;
  color: #95d0dd;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  width: 95%;
  max-width: 450px;
  height: 90vh;
  max-height: 680px;
  border-radius: 16px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #000;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  font-size: 20px;
  padding: 0;
  line-height: 1;

  &:hover {
    background-color: #333;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const LoaderText = styled.p`
  color: #666;
  font-size: 16px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Ingresos: React.FC = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [monthlyIncome, setMonthlyIncome] = useState<string>("");
  const [formattedIncome, setFormattedIncome] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showOpenBanking, setShowOpenBanking] = useState<boolean>(false);
  const [sdkLoaded, setSdkLoaded] = useState<boolean>(false);
  const [cirenioInstance, setCirenioInstance] = useState<any>(null);
  const [widgetReady, setWidgetReady] = useState<boolean>(false);
  const modalContainerId = 'cirenio-container';

  const { 
    requestUid, 
    cuil, 
    employment, 
    first_name,
    hash
  } = useFormStore((state) => ({
    requestUid: state.requestUid,
    cuil: state.cuil,
    employment: state.employment,
    first_name: state.first_name,
    hash: state.hash
  }));

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    if (numericValue === "") {
      setFormattedIncome("");
      setMonthlyIncome("");
      return;
    }

    const numberValue = parseInt(numericValue);
    const formatted = formatCurrencyUtil(numberValue);
    setFormattedIncome(formatted);
    setMonthlyIncome(numericValue);
  };

  const enviarIngresoMensual = async () => {
    if (!requestUid || !monthlyIncome) {
      setError("Faltan datos para continuar.");
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.requests.updateEmployment(requestUid, {
        monthlyIncome: parseInt(monthlyIncome),
        employmentStatus: employment || undefined,
      });

      if (response.data) {
        navigate(`/${hash}/cbu`);// Skip direct to cbu
        // setShowOpenBanking(true);
      } else {
        throw new Error("Error en la respuesta de la API");
      }
    } catch (error) {
      console.error("Error al enviar ingreso mensual:", error);
      setError("Error al enviar los datos de empleo.");
    } finally {
      setLoading(false);
    }
  };

  const iniciarOpenBanking = () => {
    if (!sdkLoaded) {
      console.error("SDK no está cargado aún");
      return;
    }

    setModalOpen(true);
  };

  useEffect(() => {
    if (modalOpen && sdkLoaded && !cirenioInstance) {
      const initializeWidget = () => {
        const container = document.getElementById(modalContainerId);
        if (!container) {
          console.error(`Container with ID "${modalContainerId}" not found`);
          return;
        }

        const CirenioSDK = (window as any).CirenioOpenBanking;
        if (!CirenioSDK || !hash || !cuil || !employment) {
          console.error("Faltan datos para iniciar el Open Banking (SDK, hash, cuil, employment).");
          return;
        }

        let cirenioBankId: number | undefined;
        try {
          const storedDataString = localStorage.getItem(`prestix_${hash}`);
          if (!storedDataString) {
            console.error(`No se encontró información en localStorage para el hash: ${hash}`);
            setModalOpen(false);
            return;
          }
          
          const storedData = JSON.parse(storedDataString);
          const bankCode = storedData.bank;

          if (!bankCode) {
             console.error("No se encontró el código del banco ('bank') en los datos de localStorage.");
             setModalOpen(false);
             return;
          }

          const bankInfo = bankOptions.find(option => option.value === bankCode);
          
          if (!bankInfo) {
            console.warn(`No se encontró el banco con código ${bankCode} en bankOptions. Se inicializará el widget sin ID de banco específico.`);
          } else if (bankInfo.cirenioId === undefined || bankInfo.cirenioId === null) {
            console.warn(`El banco con código ${bankCode} no tiene un 'cirenioId' definido en bankOptions. Se inicializará el widget sin ID de banco específico.`);
          } else {
            cirenioBankId = bankInfo.cirenioId;
            console.log(`Código de banco: ${bankCode}, Cirenio ID encontrado: ${cirenioBankId}`);
          }

        } catch (error) {
          console.error("Error al leer o procesar datos de localStorage:", error);
          setModalOpen(false);
          return;
        }

        const widgetConfig = {
          showCloseButton: false,
          employment,
          containerId: modalContainerId,
          bank: cirenioBankId
        };

        const cirenio = new CirenioSDK(
          config.sdk.openbanking.accessKey,
          hash,
          cuil,
          widgetConfig,
        );

        cirenio.on("widget_start", () => {
          console.log("Widget iniciado");
          setWidgetReady(true);
        });
        cirenio.on("flow_done", (data: any) => {
          console.log("Flujo de Open Banking completado:", data);
          setModalOpen(false);
          if (["no_bank", "missing_bank"].includes(data.type)) {
            navigate(`/${hash}/cbu`);
          } else {
            navigate(`/${hash}/buscando`);
          }
        });

        setCirenioInstance(cirenio);
        cirenio.openWidget();
      };
      
      const timeoutId = setTimeout(initializeWidget, 200);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [modalOpen, sdkLoaded, hash, cuil, employment, cirenioInstance, navigate]);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const continuarSinOpenBanking = () => {
    navigate(`/${hash}/cbu`);
  };

  useEffect(() => {
    const loadSdk = () => {
      if (!document.getElementById("cirenio-openbanking-sdk") && !sdkLoaded && config.sdk.openbanking.url) {
        const script = document.createElement("script");
        script.id = "cirenio-openbanking-sdk";
        script.src = config.sdk.openbanking.url;
        script.async = true;
        script.onload = () => {
          console.log("SDK de Open Banking cargado");
          setSdkLoaded(true);
        };
        document.body.appendChild(script);
      }
    };

    loadSdk();
  }, [showOpenBanking, sdkLoaded]);

  useEffect(() => {
    // Check if bank has Open Banking enabled
    const checkBankOpenBanking = () => {
      try {
        const storedDataString = localStorage.getItem(`prestix_${hash}`);
        if (!storedDataString) {
          console.error(`No se encontró información en localStorage para el hash: ${hash}`);
          return;
        }
        
        const storedData = JSON.parse(storedDataString);
        const bankCode = storedData.bank;

        if (!bankCode) {
          console.error("No se encontró el código del banco ('bank') en los datos de localStorage.");
          return;
        }

        const bankInfo = bankOptions.find(option => option.value === bankCode);
        
        if (!bankInfo) {
          console.warn(`No se encontró el banco con código ${bankCode} en bankOptions.`);
          return;
        }

        // If bank doesn't have Open Banking enabled, redirect to CBU screen
        if (!bankInfo.openBankingEnabled) {
          navigate(`/${hash}/cbu`);
        }
      } catch (error) {
        console.error("Error al verificar Open Banking del banco:", error);
      }
    };

    if (showOpenBanking) {
      checkBankOpenBanking();
    }
  }, [showOpenBanking, hash, navigate]);

  return (
    <Wrapper>
      {!showOpenBanking ? (
        <>
          <Lottie animationData={money} style={{ width:'200px', margin:'-10px' }}/>       
          <MainText style={{ fontWeight: "bold", fontSize: 21 }}>
            ¿Cuanto ganás por mes?
          </MainText>

          <Subtext>
            {`${first_name}, para poder ofrecerte el mejor préstamo, necesitamos conocer tus ingresos mensuales.`}
          </Subtext>

          <InfoItem style={{paddingBottom: 20 }}>
            <InfoIcon><PaymentsIcon /></InfoIcon>
            <Box>
              <Typography fontWeight={600}>Esta información es importante</Typography>
              <Typography variant="body2" mt={1}>
                <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Determinamos el monto ideal para ti
                <br />
                <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Aseguramos cuotas acordes a tu situación
              </Typography>
            </Box>
          </InfoItem>

          <TextField
            label="Ej: $550.000"
            value={formattedIncome}
            onChange={(e) => formatCurrency(e.target.value)}
            variant="outlined"
            inputProps={{
              pattern: "\\d*",
              inputMode: "numeric"
            }}
            fullWidth
            sx={{
              maxWidth: 400,
              marginBottom: 0,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "2px solid #95d0dd",
                  borderRadius: "8px",
                },
                "&:hover fieldset": {
                  borderColor: "#95d0dd",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FFF200",
                },
              },
              "& label.Mui-focused": {
                color: "#95d0dd",
              },
            }}
          />

          <Subtext style={{ fontSize: 14, marginBottom: 20 }}>Validaremos esta información para proteger tu solicitud</Subtext>

          <Button
            variant="contained"
            onClick={enviarIngresoMensual}
            disabled={loading || !monthlyIncome}
            style={{
              marginTop: 20,
              backgroundColor: "#FFF200",
              color: "black",
              fontWeight: 600,
              borderRadius: 50,
              padding: "12px 60px",
              boxShadow: "none",
              textTransform: "none",
              marginBottom: 30,
            }}
          >
            {loading ? "Cargando..." : "Continuar"}
          </Button>
        </>
      ) : (
        <>
          <Lottie animationData={banking} style={{ width:'200px', margin:'-10px' }}/>       

          <MainText style={{ fontWeight: "bold", fontSize: 21 }}>
            ¡Acelerá tu aprobación!
          </MainText>

          <Subtext>
            {first_name ? `${first_name}, podés obtener tu préstamo en menos de 3 horas conectando tu cuenta bancaria.` : 'Podés obtener tu préstamo en menos de 3 horas conectando tu cuenta bancaria.'}
          </Subtext>

          <InfoItem style={{paddingBottom: 20 }}>
            <InfoIcon><AccountBalanceIcon /></InfoIcon>
            <Box>
              <Typography fontWeight={600}>Conectando tu cuenta bancaria podés:</Typography>
              <Typography variant="body2" mt={1}>
                <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Validar automáticamente tus ingresos
                <br />
                <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Obtener una respuesta más rápida
                <br />                
                <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Aumentar las chances de aprobación
              </Typography>
            </Box>
          </InfoItem>

          <OptionWrapper>
            <VerificationButton 
              active={true}
              onClick={iniciarOpenBanking}
            >
              <ButtonContent>
                <IconWrapper active={true}>
                  <AccountBalanceIcon />
                </IconWrapper>
                <TextWrapper>
                  <ButtonTitle>Conectar mi cuenta bancaria</ButtonTitle>
                  <ButtonSubtitle>Aprobación en menos de 3 horas</ButtonSubtitle>
                </TextWrapper>
              </ButtonContent>
            </VerificationButton>

            <VerificationButton 
              onClick={continuarSinOpenBanking}
            >
              <ButtonContent>
                <IconWrapper>
                  <AccessTimeIcon />
                </IconWrapper>
                <TextWrapper>
                  <ButtonTitle>Continuar sin conectar</ButtonTitle>
                  <ButtonSubtitle>Analizaremos tu solicitud en 24 horas</ButtonSubtitle>
                </TextWrapper>
              </ButtonContent>
            </VerificationButton>
          </OptionWrapper>
        </>
      )}

      {error && (
        <Typography color="error" mt={2}>
          {error}
        </Typography>
      )}

      <ModalOverlay isOpen={modalOpen}>
        <ModalContent>
          <CloseButton onClick={handleCloseModal}>×</CloseButton>
          <ModalBody>
            {!widgetReady && (
              <Loader>
                <Spinner />
                <LoaderText>Cargando Open Banking...</LoaderText>
              </Loader>
            )}
            <div 
              id={modalContainerId} 
              style={{ width: '100%', height: '100%', display: widgetReady ? 'block' : 'none' }}
            />
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Wrapper>
  );
};

export default withAuthAndData(Ingresos);
